import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosError,
  AxiosHeaders,
} from 'axios';
import { config } from '@/config';
import { useLanguageStore } from '@/stores/language.store';
import i18n from '@/lib/i18n';
import { purgeAuthTokensAndPersist } from '@/lib/auth-bootstrap';
import { isPublicOrAuthPath } from '@/lib/public-spa-paths';

interface ApiErrorResponse {
  message?: string;
  statusCode: number;
  errors?: Record<string, string[]>;
  /** Réponses Express 404 : `{ success: false, error: 'Route not found' }` */
  error?: string;
}

const axiosInstance: AxiosInstance = axios.create({
  baseURL: config.API_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

/** Une seule promesse de refresh pour plusieurs 401 concurrents. */
let refreshSessionPromise: Promise<void> | null = null;

function queueSessionRefresh(): Promise<void> {
  if (!refreshSessionPromise) {
    refreshSessionPromise = (async () => {
      const { useAuthStore } = await import('@/stores/auth.store');
      await useAuthStore.getState().refreshAuthToken();
    })().finally(() => {
      refreshSessionPromise = null;
    });
  }
  return refreshSessionPromise;
}

// Request interceptor — attach auth token (ne pas écraser un Authorization déjà défini sur la requête)
axiosInstance.interceptors.request.use(
  (requestConfig) => {
    const existingAuthEarly =
      requestConfig.headers?.Authorization ??
      (requestConfig.headers as Record<string, unknown> | undefined)?.authorization;
    if (
      typeof window !== 'undefined' &&
      !(requestConfig as { skipDefaultAuth?: boolean }).skipDefaultAuth &&
      !existingAuthEarly &&
      isPublicOrAuthPath(window.location.pathname)
    ) {
      (requestConfig as { skipDefaultAuth?: boolean }).skipDefaultAuth = true;
    }

    if ((requestConfig as { skipDefaultAuth?: boolean }).skipDefaultAuth) {
      const locale = useLanguageStore.getState().language;
      if (requestConfig.headers) {
        requestConfig.headers['Accept-Language'] = locale;
      }
      if (requestConfig.data instanceof FormData && requestConfig.headers) {
        const h = requestConfig.headers;
        if (h instanceof AxiosHeaders) {
          h.delete('Content-Type');
        } else {
          delete (h as Record<string, unknown>)['Content-Type'];
        }
      }
      return requestConfig;
    }

    const existingAuth =
      requestConfig.headers?.Authorization ??
      (requestConfig.headers as Record<string, unknown> | undefined)?.authorization;
    if (!existingAuth) {
      const token = localStorage.getItem('auth_token');
      if (token && requestConfig.headers) {
        requestConfig.headers.Authorization = `Bearer ${token}`;
      }
    }

    const locale = useLanguageStore.getState().language;
    if (requestConfig.headers) {
      requestConfig.headers['Accept-Language'] = locale;
    }

    // FormData : ne pas envoyer application/json par défaut (sinon multer ne voit pas le fichier).
    if (requestConfig.data instanceof FormData && requestConfig.headers) {
      const h = requestConfig.headers;
      if (h instanceof AxiosHeaders) {
        h.delete('Content-Type');
      } else {
        delete (h as Record<string, unknown>)['Content-Type'];
      }
    }

    return requestConfig;
  },
  (error) => Promise.reject(error),
);

function formatAxiosApiError(status: number, data: unknown) {
  const body = data as Record<string, unknown> | undefined;
  const nestedError = body?.error;
  const nestedStringMessage = typeof nestedError === 'string' ? nestedError : undefined;
  const nestedMessage =
    nestedError && typeof nestedError === 'object' && nestedError !== null && 'message' in nestedError
      ? String((nestedError as { message: unknown }).message)
      : undefined;
  const topMessage = typeof body?.message === 'string' ? body.message : undefined;
  const nestedCode =
    nestedError && typeof nestedError === 'object' && nestedError !== null && 'code' in nestedError
      ? String((nestedError as { code: unknown }).code)
      : undefined;
  const nestedDetails =
    nestedError && typeof nestedError === 'object' && nestedError !== null && 'details' in nestedError
      ? (nestedError as { details: unknown }).details
      : undefined;
  const formattedError: {
    message: string;
    statusCode: number;
    errors?: Record<string, string[]>;
    code?: string;
    details?: unknown;
  } = {
    message: topMessage || nestedStringMessage || nestedMessage || getDefaultErrorMessage(status),
    statusCode: status,
    errors: body?.errors as Record<string, string[]> | undefined,
  };
  if (nestedCode) formattedError.code = nestedCode;
  if (nestedDetails !== undefined) formattedError.details = nestedDetails;
  return formattedError;
}

function purgeSessionAfterUnauthorized(path: string) {
  purgeAuthTokensAndPersist();
  if (isPublicOrAuthPath(path)) {
    queueMicrotask(() => {
      void Promise.all([import('@/stores/auth.store'), import('@/lib/socket')]).then(
        ([{ useAuthStore }, { disconnectSocket }]) => {
          disconnectSocket();
          try {
            useAuthStore.persist.clearStorage();
          } catch {
            /* noop */
          }
          useAuthStore.setState({
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
          });
        },
      );
    });
  } else if (!path.startsWith('/login')) {
    window.location.href = '/login';
  }
}

// Response interceptor — 401 : tenter refresh puis erreur formatée
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorResponse>) => {
    if (error.response) {
      const { status, data } = error.response;

      if (status === 401) {
        const path = window.location.pathname;
        const search = window.location.search;
        const isLearnerPlayWithToken =
          /^\/eduflow\/flows\/[^/]+\/play$/.test(path) && search.includes('accessToken=');

        const cfg = error.config;
        const reqUrl = `${cfg?.baseURL ?? ''}${cfg?.url ?? ''}`;
        const isAuthSelf =
          reqUrl.includes('/auth/refresh') ||
          reqUrl.includes('/auth/login') ||
          reqUrl.includes('/auth/register');

        const shouldTryRefresh =
          !isLearnerPlayWithToken &&
          !isAuthSelf &&
          cfg &&
          !(cfg as { __lyrraRetryAfterRefresh?: boolean }).__lyrraRetryAfterRefresh;

        if (shouldTryRefresh) {
          (cfg as { __lyrraRetryAfterRefresh?: boolean }).__lyrraRetryAfterRefresh = true;
          try {
            await queueSessionRefresh();
            const token = localStorage.getItem('auth_token');
            if (token) {
              if (!cfg.headers) cfg.headers = new AxiosHeaders();
              if (cfg.headers instanceof AxiosHeaders) {
                cfg.headers.set('Authorization', `Bearer ${token}`);
              } else {
                (cfg.headers as Record<string, string>).Authorization = `Bearer ${token}`;
              }
              return axiosInstance.request(cfg);
            }
          } catch {
            /* refresh impossible */
          }
        }

        if (!isLearnerPlayWithToken) {
          purgeSessionAfterUnauthorized(path);
        }

        return Promise.reject(formatAxiosApiError(status, data));
      }

      return Promise.reject(formatAxiosApiError(status, data));
    }

    if (error.request) {
      return Promise.reject({
        message: i18n.t('api.error.network'),
        statusCode: 0,
      });
    }

    return Promise.reject({
      message: error.message || i18n.t('api.error.unexpected'),
      statusCode: 0,
    });
  },
);

function getDefaultErrorMessage(status: number): string {
  const keyByStatus: Record<number, string> = {
    400: 'api.error.badRequest',
    403: 'api.error.forbidden',
    404: 'api.error.notFound',
    409: 'api.error.conflict',
    422: 'api.error.unprocessable',
    429: 'api.error.tooManyRequests',
    500: 'api.error.server',
    502: 'api.error.badGateway',
    503: 'api.error.maintenance',
  };
  const key = keyByStatus[status];
  return key ? i18n.t(key) : i18n.t('api.error.generic', { status });
}

export function readApiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'message' in err && typeof (err as { message: unknown }).message === 'string') {
    const msg = (err as { message: string }).message.trim();
    const details =
      'details' in err && Array.isArray((err as { details: unknown }).details)
        ? ((err as { details: unknown[] }).details as Array<{ field?: string; message?: string }>)
        : null;
    if (details?.length) {
      const fieldMsgs = details
        .map((d) => {
          const m = typeof d.message === 'string' ? d.message.trim() : '';
          const f = typeof d.field === 'string' && d.field !== '_root' ? d.field : '';
          return f && m ? `${f}: ${m}` : m || f;
        })
        .filter(Boolean)
        .join(' · ');
      if (fieldMsgs) return msg ? `${msg} — ${fieldMsgs}` : fieldMsgs;
    }
    if (msg) return msg;
  }
  if (err instanceof Error && err.message.trim()) return err.message;
  return fallback;
}

export function readApiErrorCode(err: unknown): string | undefined {
  if (err && typeof err === 'object' && 'code' in err && typeof (err as { code: unknown }).code === 'string') {
    return (err as { code: string }).code;
  }
  return undefined;
}

export const api = {
  get<T>(url: string, params?: Record<string, unknown>, axiosConfig?: AxiosRequestConfig): Promise<T> {
    return axiosInstance.get<T>(url, { params, ...axiosConfig }).then((res) => res.data);
  },

  post<T>(url: string, data?: unknown, axiosConfig?: AxiosRequestConfig): Promise<T> {
    return axiosInstance.post<T>(url, data, axiosConfig).then((res) => res.data);
  },

  put<T>(url: string, data?: unknown, axiosConfig?: AxiosRequestConfig): Promise<T> {
    return axiosInstance.put<T>(url, data, axiosConfig).then((res) => res.data);
  },

  patch<T>(url: string, data?: unknown, axiosConfig?: AxiosRequestConfig): Promise<T> {
    return axiosInstance.patch<T>(url, data, axiosConfig).then((res) => res.data);
  },

  delete<T>(url: string, axiosConfig?: AxiosRequestConfig): Promise<T> {
    return axiosInstance.delete<T>(url, axiosConfig).then((res) => res.data);
  },

  upload<T>(url: string, file: File | FormData, onProgress?: (percent: number) => void): Promise<T> {
    const formData = file instanceof FormData ? file : new FormData();
    if (file instanceof File) {
      formData.append('file', file);
    }

    return axiosInstance
      .post<T>(url, formData, {
        /** Ne pas fixer multipart/form-data sans boundary : l’intercepteur supprime Content-Type pour FormData. */
        onUploadProgress: (event) => {
          if (onProgress && event.total) {
            onProgress(Math.round((event.loaded * 100) / event.total));
          }
        },
      })
      .then((res) => res.data);
  },
};

export { axiosInstance };
export default api;
